import { elements, inputs } from '@ecosystem/newron-design-system';
import React, { useCallback, useEffect, useState } from 'react';
import constants from '../../constants';

import { CustomPriceClass } from '../../../types/content/custom.types';
import { failsRoundingCriteria, getValidInputWithinRange, isClassDuplicate, roundInput } from '../../utils';
import { CustomClassLabel } from '../customClassLabel';

const { NumberInput } = inputs;
const { MAX_CUSTOM_CLASS_TIER_VALUE, MIN_CUSTOM_CLASS_TIER_VALUE } = constants;
const { Icon } = elements;

interface CustomPriceClassesProps {
  index: number;
  priceClasses: CustomPriceClass[]; //to check duplicate
  prevMax: number;
  nextMax: number;
  onPriceChange: (index: number) => (value: number) => void;
  onLabelChange: (index: number) => (label: string) => void;
  labelError?: string;
}
export const CustomPriceClasses: React.FC<CustomPriceClassesProps> = ({
  index,
  priceClasses,
  prevMax,
  nextMax,
  onPriceChange,
  onLabelChange,
  labelError,
}: CustomPriceClassesProps) => {
  const priceClass = priceClasses[index];
  const [maxValue, setMaxValue] = useState<number>(priceClass.max);
  const [hasError, setHasError] = useState<boolean>(false);
  const priceOnChangeFn = onPriceChange(index);

  useEffect(() => {
    //on re-rendering of customClasses if duplicate exist update error state.
    updateClassErrorState(priceClasses);

    setMaxValue(Number(priceClass.max?.toFixed(2))); // Update maxValue when classToRender.max changes
  }, [priceClasses, priceClass]);

  /**
   * Function to check if the input class has errors exists while re-rendering custom classes.
   * @param customClasses the input
   */
  const updateClassErrorState = useCallback(
    (customClasses: CustomPriceClass[]) => {
      // checked this way as if (classToRender) will return false for 0
      if (priceClass.max !== undefined && priceClass.max !== null) {
        const duplicateClassExist = isClassDuplicate(priceClass, customClasses);
        if (duplicateClassExist || priceClass.max < priceClass.min) {
          setHasError(true);
          return;
        }
        setHasError(false);
      }
    },
    [priceClass]
  );

  const handleRangeChange = useCallback(
    (input: number): void => {
      const validInputWithinRange = getValidInputWithinRange(input);
      //Here we will check if the input is within the min and max allowed custom tier
      if (input < validInputWithinRange) {
        setHasError(true);
        setMaxValue(input);
        return;
      }
      setHasError(failsRoundingCriteria(input, priceClass.roundTo));
      setMaxValue(validInputWithinRange);
    },
    [priceClass.roundTo]
  );

  const handleRangeBlur = (inputMax: number): void => {
    // we aren't using !input as this would return true if input was 0
    if (inputMax === undefined || inputMax === null) return;

    // if roundTo is present we always round the input and remove the error
    priceOnChangeFn(roundInput(inputMax, prevMax, nextMax, priceClass).roundedValue);
    setHasError(false);
  };

  /**
   * Function to return what to render in the higher value input, either a infinite sign or a number input
   * @returns JSX.Element
   */
  const renderHigherValue = () => {
    if (index === priceClasses.length - 1) {
      return (
        <div className="pl-8 pt-2">
          <Icon color="inactive" size="lg" testId="infinite-icon">
            <Icon.icons.Infinite />
          </Icon>
        </div>
      );
    }
    return (
      <div className="w-2/4">
        <NumberInput
          key={`max-price-${index}`}
          value={maxValue}
          onChange={input => handleRangeChange(input)}
          onBlur={() => handleRangeBlur(maxValue)}
          testId={`max-price-${index}`}
          hasError={hasError}
          minValue={MIN_CUSTOM_CLASS_TIER_VALUE}
          maxValue={MAX_CUSTOM_CLASS_TIER_VALUE}
          step={Number(priceClass.roundTo)}
        />
      </div>
    );
  };

  return (
    <div className="pt-5">
      <div className="relative pb-2" data-test-id={'price'}>
        <div className="flex items-start gap-2">
          <div className="w-2/4">
            <NumberInput
              key={`min-price-${index}`}
              value={priceClass.min}
              disabled={true}
              onChange={() => {}}
              testId={`min-price-${index}`}
              minValue={MIN_CUSTOM_CLASS_TIER_VALUE}
              maxValue={MAX_CUSTOM_CLASS_TIER_VALUE}
            />
          </div>

          <span className="pt-3">—</span>
          {renderHigherValue()}
        </div>
      </div>
      <CustomClassLabel
        customClasses={priceClasses}
        labelOnChangeFn={onLabelChange(index)}
        index={index}
        isLabelOptional={true}
        errorMessage={labelError}
      />
    </div>
  );
};
